import { apiUrl } from "../utils/api";
import { cached, invalidateByPrefix, peek } from "../utils/swrCache";
import type { MailboxAccess } from "./graphMailService";
// Nur ein TYP-Import (wird beim Bauen entfernt) — kein Laufzeit-Zyklus, obwohl
// calendarTerminService seinerseits aus dieser Datei importiert.
import type { TerminArt } from "./calendarTerminService";
import { reportMailError } from "./mailError";

// --- Serientermin + Erinnerung (SP4) — Mirror der BE-DTOs ---
export type RecurrenceFrequency = "daily" | "weekly" | "monthly" | "yearly";
export type WeekdayName =
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday"
    | "sunday";
export type RecurrenceRangeType = "noEnd" | "endDate" | "numbered";

/** Serientermin (KMVS-Form, 1:1 zum BE-`RecurrenceDto`). null = einmalig. */
export interface RecurrenceInput {
    pattern: { type: RecurrenceFrequency; interval: number; daysOfWeek?: WeekdayName[] };
    range: {
        type: RecurrenceRangeType;
        startDate: string; // "YYYY-MM-DD"
        endDate?: string;
        numberOfOccurrences?: number;
        recurrenceTimeZone?: string;
    };
}

/** Graph-Instanztyp (READ). Serien-Indikator-Quelle (Konsistenz C4). */
export type CalendarEventType = "singleInstance" | "occurrence" | "exception" | "seriesMaster";

export interface CalendarEvent {
    id: string;
    /**
     * Graph iCalUId — postfachübergreifend stabile Termin-Identität (BE-Mirror,
     * Termine SP3). Dedupe-Schlüssel für Overlay-Dubletten = iCalUId + Start
     * (Serieninstanzen teilen die UID des Serien-Masters).
     */
    iCalUId: string | null;
    subject: string;
    start: { dateTime: string; timeZone: string };
    end: { dateTime: string; timeZone: string };
    isAllDay: boolean;
    location: string;
    bodyPreview: string;
    organizer: { name: string; address: string } | null;
    attendees: { name: string; address: string; type: string; response: string }[];
    isOnlineMeeting: boolean;
    onlineMeetingUrl: string | null;
    /** Graph showAs: free|tentative|busy|oof|workingElsewhere|unknown (BE-Mirror, SP2 Grid-Lesbarkeit). */
    showAs: string | null;
    /**
     * Graph sensitivity: normal|personal|private|confidential (PO 27.07.2026).
     *
     * Steht auch am MASKIERTEN Termin — daran erkennt die Ansicht, dass „Privat"
     * eine Maske ist und kein echter Betreff. Die Maskierung selbst passiert im
     * Backend (termin-privat.ts), nicht hier.
     */
    sensitivity: string | null;
    // --- Serientermin + Erinnerung (SP4, READ) ---
    /** Serien-Muster; null = kein Serientermin. Zuverlässig nur über getEvent. */
    recurrence: RecurrenceInput | null;
    isReminderOn: boolean;
    reminderMinutesBeforeStart: number | null;
    type: CalendarEventType;
    seriesMasterId: string | null;
}

export interface CalendarEventDetail extends CalendarEvent {
    body: { contentType: string; content: string } | null;
}

/** Bookable room as returned by the Graph places API (calendar/rooms). */
export interface RoomDto {
    displayName: string;
    emailAddress: string;
    capacity: number | null;
    building: string | null;
    floorLabel: string | null;
}

/** Verfügbarkeit eines Raums im Zeitraum (GET calendar/rooms/verfuegbarkeit). */
export interface RaumVerfuegbarkeit {
    emailAddress: string;
    status: "frei" | "belegt" | "unbekannt";
    /** Belegte Zeitblöcke im Zeitraum (ISO, UTC) — ohne Betreff. */
    belegt: { start: string; end: string }[];
}

export interface RaumVerfuegbarkeitAntwort {
    /** false = Graph gerade nicht erreichbar. */
    geprueft: boolean;
    raeume: RaumVerfuegbarkeit[];
}

export interface ScheduleBlock {
    start: string;
    end: string;
    status: string;
}

/** Free/busy result for one mailbox/room (calendar/schedule). */
export interface ScheduleResult {
    scheduleId: string;
    availabilityView: string;
    blocks: ScheduleBlock[];
    error: string | null;
}

/** Abwesenheits-Typ aus dem Titel-Keyword (absence-classify, TER-D3). */
export type InviteWarningType = "urlaub" | "krank" | "homeoffice" | "abwesend";

/** Ein Abwesenheits-Treffer für die nicht-blockierende Einladungs-Warnung (TER-D6). */
export interface InviteWarning {
    mailbox: string;
    type: InviteWarningType;
    from: string;
    to: string;
}

/** Origin calendar a merged event came from, used for colour-coding the overlay. */
export interface CalendarEventSource {
    address: string;
    displayName: string;
    color: string;
}

/** A calendar event enriched with the calendar (mailbox) it originated from. */
/**
 * K9 — KMVS-Sidecar-Daten zu einem Graph-Termin, so weit der Betrachter sie
 * sehen darf. `vorgangTitel` bleibt null, wenn der Vorgang für ihn nicht lesbar
 * ist (persönlicher Vorgang, fremdes Team); die `vorgangId` bleibt sichtbar.
 */
export interface TerminKmvsMeta {
    terminId: number;
    vorgangId: number | null;
    vorgangTitel: string | null;
    terminArt: TerminArt | null;
    farbe: string | null;
    /**
     * Die dem Termin zugewiesenen Kontakte (PO 21.09.2026), Hauptkontakt zuerst.
     * `undefined` heißt „nicht mitgeladen", `[]` heißt „keine Zuweisung".
     */
    kontakte?: TerminKontakt[];
    /**
     * T-309 (13.10.2026): die Notiz-Seite des Termins — nur der Kontakt-Reiter
     * „Termine" lädt sie mit. Fehlt ohne Seite mit Inhalt und bei privaten
     * Terminen fremder Postfächer.
     */
    notiz?: TerminNotiz | null;
}

/** Die Notiz-Seite eines Termins, wie eine Terminliste sie mitliefert (T-309). */
export interface TerminNotiz {
    /** Serverseitig bereinigtes HTML; null = nur eine Zeichnung. */
    contentHtml: string | null;
    updatedAt: string | null;
    hatZeichnung: boolean;
}

/** Ein dem Termin zugewiesener Kontakt (PO 21.09.2026). */
export interface TerminKontakt {
    contactId: number;
    name: string;
    contactType: string | null;
    /** Der Hauptkontakt — steht in der Chip-Reihe vorn. */
    haupt: boolean;
}

/**
 * Warum ein Termin in der Akte eines Kontakts steht, obwohl er einem ANDEREN
 * Kontakt gehört — die Grundlage des „über &lt;Firma&gt;"-Badges im
 * Termine-Reiter. Dieselben Ebenen wie im Vorgänge-Reiter.
 */
export interface TerminHerkunft {
    contactId: number;
    name: string | null;
    /** `ansprechpartner` (28.09.2026): Person an der Firma bzw. an einer Verbundfirma. */
    ebene: "firma" | "verbund" | "haushalt" | "ansprechpartner";
    /** Nur bei `ansprechpartner` einer Verbundfirma: deren Name. */
    firmaName?: string | null;
}

export interface SourcedCalendarEvent extends CalendarEvent {
    source: CalendarEventSource;
    /** K9 — Sidecar-Meta; null/fehlend ohne Sidecar. */
    kmvs?: TerminKmvsMeta | null;
}

/**
 * SP7 (ContactDetails-Termine): ein Agenda-Termin eines Kontakts, getaggt mit dem
 * Quell-Postfach. Anders als {@link SourcedCalendarEvent} trägt `source` KEINE Farbe
 * (die Agenda-Liste färbt nicht nach Kalender) — der Backend liefert nur
 * address/displayName.
 */
export interface ContactTerminEvent extends CalendarEvent {
    source: { address: string; displayName: string };
    /**
     * K9 — Sidecar-Meta des Termins (Vorgangs-Badge in den adressbasierten
     * Listen). null/fehlend, wenn kein Sidecar existiert.
     */
    kmvs?: TerminKmvsMeta | null;
    /**
     * Der Termin gehört nicht dem geöffneten Kontakt, sondern kommt über diesen
     * herein („über &lt;Firma&gt;"). null/fehlend = eigener Termin.
     */
    via?: TerminHerkunft | null;
    /**
     * Dem Kontakt ausdrücklich zugewiesen — im Unterschied zu einem, der nur
     * über die Teilnehmeradressen gefunden wurde.
     */
    zugewiesen?: boolean;
}

/**
 * Über welchen Kontakt die Nutzerin einen Termin der Ebene „Termine meiner
 * Kontakte" sieht (PO 28.09.2026): sie ist FO oder BO dieses Kontakts, und der
 * Kontakt ist dem Termin zugewiesen (`zuweisung`) oder steht mit einer seiner
 * Adressen im Verteiler (`teilnehmer`).
 */
export interface UeberKontakt {
    contactId: number;
    name: string;
    weg: "zuweisung" | "teilnehmer";
}

/** Ein Termin der Kalender-Ebene „Termine meiner Kontakte" (nur lesen). */
export interface KontaktTerminEvent extends CalendarEvent {
    source: { address: string; displayName: string };
    kmvs?: TerminKmvsMeta | null;
    ueberKontakt: UeberKontakt;
}

export interface CreateEventInput {
    mailbox: string;
    subject: string;
    start: string;
    end: string;
    timeZone?: string;
    isAllDay?: boolean;
    location?: string;
    body?: string;
    contentType?: "Text" | "HTML";
    attendees?: { address: string; name?: string; type?: "required" | "optional" | "resource" }[];
    isOnlineMeeting?: boolean;
    /** Privater Termin — in Outlook als privat, in KMVS für Kollegen maskiert. */
    istPrivat?: boolean;
    // --- Serientermin + Erinnerung (SP4) ---
    recurrence?: RecurrenceInput | null;
    isReminderOn?: boolean;
    reminderMinutesBeforeStart?: number;
}

export interface UpdateEventInput {
    subject?: string;
    start?: string;
    end?: string;
    timeZone?: string;
    isAllDay?: boolean;
    location?: string;
    body?: string;
    contentType?: "Text" | "HTML";
    attendees?: { address: string; name?: string; type?: "required" | "optional" | "resource" }[];
    /** Privater Termin — in Outlook als privat, in KMVS für Kollegen maskiert. */
    istPrivat?: boolean;
    // --- Serientermin + Erinnerung (SP4) ---
    // recurrence: null ⇒ Serie auflösen; weglassen ⇒ unangetastet.
    recurrence?: RecurrenceInput | null;
    isReminderOn?: boolean;
    reminderMinutesBeforeStart?: number;
}

const fetchJson = async <T>(input: string, init?: RequestInit): Promise<T> => {
    const res = await fetch(input, init);
    if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`${res.status} ${res.statusText}: ${text}`);
    }
    return res.json();
};

const ensureMailbox = (mailbox: string | null | undefined): string => {
    if (!mailbox) throw new Error("Kein Postfach ausgewählt");
    return mailbox;
};

// ── SWR-Cache für Termin-Listen (Termine-SP4) ──────────────────────────────
// Bisher lud JEDER View-/Datumswechsel alle gewählten Postfächer neu — `calendar/
// events` ist teuer (ein Graph-Roundtrip je Postfach). `listEvents` läuft nun
// über den SWR-Cache, Key je (Postfach × Range × top). `persist:false`
// (memory-only): N Postfächer × Ranges sprengen die localStorage-Quota — und
// memory-only behält die echten Objekte (kein JSON-Rehydrat). Ein
// `reviveCalendarEventDates` wäre ohnehin ein No-Op: `CalendarEvent.start/end`
// sind ISO-STRINGS (kein Date-Feld im DTO); die Date-Wandlung passiert erst
// downstream im CalendarStateContext (toGraphDate/graphAllDayDate).
const CAL_EVENTS_PREFIX = "cal:events:";
const CAL_EVENTS_TTL_MS = 3 * 60_000; // 3 min; Fokus-/Schreib-Invalidierung heilt Frischeres sofort.

const toIsoStr = (v: string | Date): string => (typeof v === "string" ? v : v.toISOString());

/** Cache-Key einer Termin-Liste: Postfach (lowercased) × Range × top. */
const calEventsKey = (
    mailbox: string,
    from: string | Date,
    to: string | Date,
    top: number,
): string => `${CAL_EVENTS_PREFIX}${mailbox.toLowerCase()}:${toIsoStr(from)}:${toIsoStr(to)}:${top}`;

/**
 * Verwirft ALLE gecachten Termin-Listen eines Postfachs (alle Ranges/tops).
 * Nach jedem Schreibpfad aufrufen — Create/Update/Delete (Update deckt auch
 * Drag/Resize ab) hängen sie unten selbst an; die Sidecar-Schreibpfade
 * (Art/Farbe) rufen sie aus dem calendarTerminService. Sonst zeigt der nächste
 * Reload den alten Cache-Stand.
 */
export function invalidateCalendarMailbox(mailbox: string): void {
    invalidateByPrefix(`${CAL_EVENTS_PREFIX}${mailbox.toLowerCase()}:`);
}

/**
 * Peek ohne Refetch: liegt für (Postfach × Range × top) bereits ein Cache-
 * Eintrag vor? Der CalendarStateContext unterscheidet damit Erst-Load (kalt →
 * Skeleton) von Revalidierung (Cache-Treffer → nur dezenter Hinweis).
 */
export function peekCalendarEvents(
    mailbox: string,
    from: string | Date,
    to: string | Date,
    top = 100,
): CalendarEvent[] | undefined {
    return peek<CalendarEvent[]>(calEventsKey(mailbox, from, to, top));
}

export const calendarService = {
    async listMailboxes(): Promise<MailboxAccess[]> {
        return fetchJson<MailboxAccess[]>(apiUrl("calendar/mailboxes"));
    },

    /**
     * SP7: geräteübergreifend persistierte Default-/Auswahl-Kalender des
     * eingeloggten Nutzers. Leeres Array, wenn noch nichts gespeichert ist.
     */
    async getCalendarPreferences(): Promise<{ selectedCalendars: string[] }> {
        return fetchJson<{ selectedCalendars: string[] }>(apiUrl("calendar/preferences"));
    },

    /**
     * SP7: Auswahl-Set speichern (best-effort vom MailContext aufgerufen). Das
     * Backend filtert serverseitig auf Kalender mit Lesezugriff und gibt das
     * tatsächlich persistierte Set zurück.
     */
    async saveCalendarPreferences(
        addresses: string[],
    ): Promise<{ selectedCalendars: string[] }> {
        return fetchJson<{ selectedCalendars: string[] }>(apiUrl("calendar/preferences"), {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ selectedCalendars: addresses }),
        });
    },

    async listEvents(mailbox: string, from: string | Date, to: string | Date, top = 100): Promise<CalendarEvent[]> {
        const mb = ensureMailbox(mailbox);
        const fromIso = toIsoStr(from);
        const toIso = toIsoStr(to);
        // SWR (SP4): Cache-Treffer rendert sofort, teurer Graph-Call nur bei Miss/
        // Stale. persist:false — siehe Kopfkommentar (Quota + echte Objekte).
        return cached(
            calEventsKey(mb, fromIso, toIso, top),
            () =>
                fetchJson<CalendarEvent[]>(
                    apiUrl("calendar/events", { mailbox: mb, from: fromIso, to: toIso, top }),
                ),
            { ttlMs: CAL_EVENTS_TTL_MS, persist: false },
        );
    },

    async listRooms(): Promise<RoomDto[]> {
        return fetchJson<RoomDto[]>(apiUrl("calendar/rooms"));
    },

    /**
     * Belegung aller wählbaren Räume im Zeitraum. Beim Bearbeiten `mailbox` +
     * `eventId` mitgeben, dann zählt der eigene Termin nicht als Belegung.
     */
    async roomAvailability(
        start: Date,
        end: Date,
        eigenerTermin?: { mailbox: string; eventId: string } | null,
    ): Promise<RaumVerfuegbarkeitAntwort> {
        const params: Record<string, string> = {
            start: start.toISOString(),
            end: end.toISOString(),
        };
        if (eigenerTermin?.mailbox && eigenerTermin.eventId) {
            params.mailbox = eigenerTermin.mailbox;
            params.eventId = eigenerTermin.eventId;
        }
        return fetchJson<RaumVerfuegbarkeitAntwort>(apiUrl("calendar/rooms/verfuegbarkeit", params));
    },

    async getSchedule(
        organizerMailbox: string,
        schedules: string[],
        from: string | Date,
        to: string | Date,
        interval = 30,
    ): Promise<ScheduleResult[]> {
        const fromIso = typeof from === "string" ? from : from.toISOString();
        const toIso = typeof to === "string" ? to : to.toISOString();
        return fetchJson<ScheduleResult[]>(apiUrl("calendar/schedule"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                organizerMailbox: ensureMailbox(organizerMailbox),
                schedules,
                from: fromIso,
                to: toIso,
                availabilityViewInterval: interval,
            }),
        });
    },

    /**
     * Einladungs-Warnung (TER-D6, nicht blockierend): prüft je internem
     * Teilnehmer-Postfach, ob im Termin-Zeitraum eine ganztägige Abwesenheit
     * (Titel-Keyword) liegt. NUR interne KMVS-Postfächer übergeben — externe
     * Kunden werden nicht geprüft. Best-effort: bei Fehler leere Liste.
     */
    async getInviteWarnings(
        attendees: string[],
        from: string | Date,
        to: string | Date,
    ): Promise<InviteWarning[]> {
        if (attendees.length === 0) return [];
        const fromIso = typeof from === "string" ? from : from.toISOString();
        const toIso = typeof to === "string" ? to : to.toISOString();
        return fetchJson<InviteWarning[]>(apiUrl("calendar/invite-warnings"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attendees, from: fromIso, to: toIso }),
        });
    },

    /**
     * Load events for several calendars in parallel and tag each event with the
     * calendar it came from. Calendars that fail (no access, Graph error) are
     * skipped via Promise.allSettled so one bad mailbox can't blank the overlay.
     */
    async listEventsForMailboxes(
        mailboxes: { address: string; displayName: string; color: string }[],
        from: string | Date,
        to: string | Date,
        top = 200,
    ): Promise<SourcedCalendarEvent[]> {
        const results = await Promise.allSettled(
            mailboxes.map(async (mb) => {
                const list = await this.listEvents(mb.address, from, to, top);
                return list.map<SourcedCalendarEvent>((ev) => ({
                    ...ev,
                    source: { address: mb.address, displayName: mb.displayName, color: mb.color },
                }));
            }),
        );
        const events: SourcedCalendarEvent[] = [];
        results.forEach((res) => {
            if (res.status === "fulfilled") {
                events.push(...res.value);
            } else {
                reportMailError({
                    area: "mail-calendar",
                    action: "load-calendar",
                    error: res.reason,
                    fallbackMessage: "Ein Kalender konnte nicht geladen werden.",
                });
            }
        });
        return events;
    },

    /**
     * SP7 (ContactDetails-Termine): Agenda-Termine eines Kontakts. Der Backend löst
     * die relevanten Adressen des Kontakts (Firma: Zentrale + AP-geschäftlich; Person:
     * geschäftlich + privat) auf und filtert alle lesbaren Kalender im Zeitraum.
     * `from`/`to` optional — ohne Angabe gilt serverseitig das Fenster −30/+90 Tage.
     */
    async listContactTermine(
        contactId: number,
        from?: string | Date,
        to?: string | Date,
        opts?: {
            /**
             * Zugewiesene Termine: fehlt = nur im Zeitfenster (Bestand),
             * `alle` = ohne Zeitgrenze, `keine` = nur Adressfunde (Nachlade-Stücke).
             */
            zuweisungen?: "alle" | "keine";
            signal?: AbortSignal;
        },
    ): Promise<ContactTerminEvent[]> {
        const params: Record<string, string | number> = { contactId };
        if (from !== undefined) params.from = typeof from === "string" ? from : from.toISOString();
        if (to !== undefined) params.to = typeof to === "string" ? to : to.toISOString();
        if (opts?.zuweisungen) params.zuweisungen = opts.zuweisungen;
        return fetchJson<ContactTerminEvent[]>(
            apiUrl("calendar/contact-termine", params),
            opts?.signal ? { signal: opts.signal } : undefined,
        );
    },

    /**
     * C1 (Mail-Kontakt-Sidebar, Reiter „Termine"): gemeinsame Termine zu einer
     * freien Adressliste — braucht KEINEN KMVS-Kontakt. Der Backend filtert alle
     * lesbaren Kalender nach Terminen, an denen eine der Adressen teilnimmt.
     * `from`/`to` optional — ohne Angabe gilt serverseitig jetzt … +1 Jahr
     * (nur ZUKÜNFTIGE Termine, kein Vergangenheits-Flooding).
     */
    async listCommonTermineByAddresses(
        addresses: string[],
        from?: string | Date,
        to?: string | Date,
    ): Promise<ContactTerminEvent[]> {
        const body: { addresses: string[]; from?: string; to?: string } = { addresses };
        if (from !== undefined) body.from = typeof from === "string" ? from : from.toISOString();
        if (to !== undefined) body.to = typeof to === "string" ? to : to.toISOString();
        return fetchJson<ContactTerminEvent[]>(apiUrl("calendar/contact-termine/by-address"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(body),
        });
    },

    /**
     * Kalender-Ebene „Termine meiner Kontakte" (PO 28.09.2026): Termine aus
     * Postfächern, die die Nutzerin NICHT lesen darf, an denen ein Kontakt
     * beteiligt ist, für den sie FO oder BO ist. Nur lesen — Ändern bleibt
     * beim Kalender-Schreibrecht. Private Termine fremder Postfächer fehlen.
     */
    async listKontaktTermine(from: string | Date, to: string | Date): Promise<KontaktTerminEvent[]> {
        return fetchJson<KontaktTerminEvent[]>(
            apiUrl("calendar/kontakt-termine", {
                from: typeof from === "string" ? from : from.toISOString(),
                to: typeof to === "string" ? to : to.toISOString(),
            }),
        );
    },

    async getEvent(mailbox: string, eventId: string): Promise<CalendarEventDetail> {
        return fetchJson<CalendarEventDetail>(
            apiUrl(`calendar/events/${encodeURIComponent(eventId)}`, { mailbox: ensureMailbox(mailbox) }),
        );
    },

    async createEvent(input: CreateEventInput): Promise<CalendarEventDetail> {
        const mailbox = ensureMailbox(input.mailbox);
        const created = await fetchJson<CalendarEventDetail>(apiUrl("calendar/events"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...input, mailbox }),
        });
        // Neuer Termin ⇒ gecachte Listen des Postfachs verwerfen (SP4).
        invalidateCalendarMailbox(mailbox);
        return created;
    },

    async updateEvent(mailbox: string, eventId: string, patch: UpdateEventInput): Promise<CalendarEventDetail> {
        const mb = ensureMailbox(mailbox);
        const updated = await fetchJson<CalendarEventDetail>(
            apiUrl(`calendar/events/${encodeURIComponent(eventId)}`, { mailbox: mb }),
            {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(patch),
            },
        );
        // Zeit/Titel/Serie geändert — auch Drag/Resize (handleEventChange) läuft
        // hierüber ⇒ Listen-Cache verwerfen (SP4).
        invalidateCalendarMailbox(mb);
        return updated;
    },

    async deleteEvent(mailbox: string, eventId: string): Promise<void> {
        const mb = ensureMailbox(mailbox);
        await fetchJson(
            apiUrl(`calendar/events/${encodeURIComponent(eventId)}`, { mailbox: mb }),
            { method: "DELETE" },
        );
        invalidateCalendarMailbox(mb);
    },

    /**
     * Rangeübergreifende Backend-Suche über mehrere Postfächer in EINEM Call
     * (SP5, Konsistenz D1 — eigener `POST /calendar/search`, nicht Param am
     * range-gebundenen `calendar/events`). Das Backend taggt jeden Treffer mit
     * `source.address`/`source.displayName`; die Quellfarbe setzt das FE hier
     * über die übergebene `color` (analog `listEventsForMailboxes`). Ohne
     * `from`/`to` sucht das Backend rangeübergreifend.
     */
    async searchEvents(
        mailboxes: { address: string; displayName: string; color: string }[],
        search: string,
        opts?: { from?: string | Date; to?: string | Date; top?: number },
    ): Promise<SourcedCalendarEvent[]> {
        const term = search.trim();
        if (term.length === 0 || mailboxes.length === 0) return [];
        const colorByAddress = new Map(
            mailboxes.map((m) => [m.address.toLowerCase(), m.color] as const),
        );
        const toIso = (v: string | Date | undefined): string | undefined =>
            v === undefined ? undefined : typeof v === "string" ? v : v.toISOString();
        const raw = await fetchJson<
            (CalendarEvent & { source: { address: string; displayName: string } })[]
        >(apiUrl("calendar/search"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                mailboxes: mailboxes.map((m) => m.address),
                search: term,
                from: toIso(opts?.from),
                to: toIso(opts?.to),
                top: opts?.top,
            }),
        });
        // BE liefert nur address/displayName → FE ergänzt die Quellfarbe.
        return raw.map<SourcedCalendarEvent>((ev) => ({
            ...ev,
            source: {
                address: ev.source.address,
                displayName: ev.source.displayName,
                color: colorByAddress.get(ev.source.address.toLowerCase()) ?? "#999999",
            },
        }));
    },
};
